import { z } from 'zod';
import { zPrinterInterface, zPrinterSdk } from '../enums';

/**
 * Identifier of the docket viewer printer (`interface: 'SCREEN'`). Not a device: it never appears
 * in discovery, is always online, and its dockets are read on a phone through the docket viewer
 * instead of being printed. Only ever a direct destination, for a venue with no physical printer
 * (and a printer-less demo); never a fallback for a physical printer that is offline.
 *
 * Never added automatically: a new POS config starts with no printers, and only a super admin
 * adds it from admin-web while the venue has no physical (USB/LAN) printer. It never coexists
 * with one, online or offline: pairing a physical printer removes it. An admin can delete it (a
 * venue with zero printers is valid). Otherwise an ordinary printer: name and templates are
 * editable, and templates created later are not assigned to it automatically.
 *
 * Only used to create the row. Rows added before this value was introduced keep `SCREEN_PRINTER`,
 * so recognise the printer with `isDocketViewerPrinter`, never by identifier.
 */
export const DOCKET_VIEWER_PRINTER_IDENTIFIER = 'DOCKET_VIEWER';

/** Shown wherever printers are listed, matching the name the POS and the phone app both use. */
export const DOCKET_VIEWER_PRINTER_NAME = 'Docket viewer';

export const isDocketViewerPrinter = (printer: { interface: string }): boolean =>
  printer.interface === 'SCREEN';

export const posPrinterSchema = z.object({
  /**
   * For a USB printer, the `uuid` of the iPad it is physically plugged into (a USB printer is
   * attached to exactly one device). `null` for LAN printers and unattached printers. This is the
   * single owner-authored, posConfig-coupled device fact; its integrity (each iPad has ≤1 USB
   * printer) is a single-document `posConfigStrictSchema` refine.
   */
  attachedToIPadUuid: z.string().nullable().default(null).optional(),
  /**
   * When true, the printer's buzzer sounds an alarm after printing a kitchen docket.
   * Buzzer connects to RJ11 port on the printer.
   **/
  buzzerEnabled: z.boolean().optional(),
  /** if `discoveryFailedAt` is null, then the printer is ONLINE. */
  discoveryFailedAt: z.number().nullable().default(null),
  docketTemplateUuids: z.array(z.string()).default([]),
  identifier: z.string(),
  interface: zPrinterInterface,
  model: z.string().nullable().default(null),
  name: z.string().default('Main printer'),
  /** Which native SDK discovered this printer and is used to communicate with it. */
  sdk: zPrinterSdk.default('STAR_MICRONICS').optional(),

  createdAt: z.number().optional(),
  updatedAt: z.number().optional(),
});
export type PosPrinter = z.output<typeof posPrinterSchema>;

/**
 * The docket viewer printer row before it is saved. Stored holding every docket template when a super
 * admin adds it from admin-web (see `DOCKET_VIEWER_PRINTER_IDENTIFIER`).
 */
export const DEFAULT_DOCKET_VIEWER_PRINTER: PosPrinter = {
  attachedToIPadUuid: null,
  discoveryFailedAt: null,
  docketTemplateUuids: [],
  identifier: DOCKET_VIEWER_PRINTER_IDENTIFIER,
  interface: 'SCREEN',
  model: null,
  name: DOCKET_VIEWER_PRINTER_NAME,
};

export const posPrinterStrictSchema = posPrinterSchema.safeExtend({
  docketTemplateUuids: z.array(z.guid()),
});

/** When printer SDK discovers a printer, it will return this shape, which is a subset of `PosPrinter` */
export const posPrinterDiscoveredSchema = posPrinterSchema.pick({
  identifier: true,
  interface: true,
  model: true,
  sdk: true,
});
export type PosPrinterDiscovered = z.output<typeof posPrinterDiscoveredSchema>;

export const posPrinterCvxSchema = posPrinterSchema.safeExtend({
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type PosPrinterCvx = z.output<typeof posPrinterCvxSchema>;
